import React from 'react';
import {
  SleepSession,
  SleepStageType,
  STAGE_COLORS,
  STAGE_LABELS,
} from '../services/sleepService';

interface SleepSessionCardProps {
  session: SleepSession;
}

const formatLocalTime = (iso: string, timezone: string) => {
  try {
    return new Date(iso).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone || undefined,
    });
  } catch {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }
};

const formatDuration = (start: string, end: string) => {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const totalMinutes = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
};

const StageTimeline: React.FC<{ session: SleepSession }> = ({ session }) => {
  const sessionStart = new Date(session.start_time).getTime();
  const sessionEnd = new Date(session.end_time).getTime();
  const totalMs = Math.max(1, sessionEnd - sessionStart);

  const stages = [...(session.stages || [])].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );

  if (stages.length === 0) {
    return (
      <div style={{ fontSize: 12, color: '#6b7280', padding: '12px 0' }}>
        Sin datos de fases para esta sesión.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 28,
          background: '#f1f5f9',
          borderRadius: 4,
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        {stages.map((stage) => {
          const start = new Date(stage.start_time).getTime();
          const end = new Date(stage.end_time).getTime();
          const leftPct = ((start - sessionStart) / totalMs) * 100;
          const widthPct = ((end - start) / totalMs) * 100;
          return (
            <div
              key={stage.id}
              title={`${STAGE_LABELS[stage.stage_type]} · ${stage.duration_minutes.toFixed(0)} min`}
              style={{
                position: 'absolute',
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                top: 0,
                bottom: 0,
                background: STAGE_COLORS[stage.stage_type],
              }}
            />
          );
        })}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: '#6b7280',
          marginTop: 4,
        }}
      >
        <span>{formatLocalTime(session.start_time, session.timezone)}</span>
        <span>{formatLocalTime(session.end_time, session.timezone)}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
        {(Object.keys(STAGE_LABELS) as SleepStageType[]).map((s) => {
          const total = stages
            .filter((st) => st.stage_type === s)
            .reduce((acc, st) => acc + (st.duration_minutes || 0), 0);
          if (total === 0) return null;
          return (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: STAGE_COLORS[s],
                  borderRadius: 2,
                  display: 'inline-block',
                }}
              />
              <span>
                {STAGE_LABELS[s]}: {Math.round(total)} min
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: 14, color: '#1f2937' }}>{value}</div>
  </div>
);

const SleepSessionCard: React.FC<SleepSessionCardProps> = ({ session }) => {
  const isMain = session.kind === 'MAIN';
  const badgeBg = isMain ? '#1e3a8a' : '#8b5cf6';
  const badgeLabel = isMain ? 'Principal' : 'Siesta';

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              background: badgeBg,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 4,
              textTransform: 'uppercase',
            }}
          >
            {badgeLabel}
          </span>
          <span style={{ fontWeight: 600, color: '#222' }}>
            {formatLocalTime(session.start_time, session.timezone)} →{' '}
            {formatLocalTime(session.end_time, session.timezone)}
          </span>
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            ({formatDuration(session.start_time, session.end_time)})
          </span>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
        }}
      >
        <Metric
          label="Eficiencia"
          value={session.efficiency !== null ? `${session.efficiency.toFixed(1)}%` : '-'}
        />
        <Metric
          label="Latencia"
          value={session.latency_minutes !== null ? `${session.latency_minutes} min` : '-'}
        />
        <Metric
          label="WASO"
          value={session.waso_minutes !== null ? `${session.waso_minutes} min` : '-'}
        />
        <Metric label="Dispositivo" value={session.source_device || '-'} />
        <Metric label="App" value={session.source_app || '-'} />
      </div>

      <StageTimeline session={session} />
    </div>
  );
};

export default SleepSessionCard;
